import { memo, useCallback, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { cn } from "@/lib/cn";

interface GlowingEffectProps {
  blur?: number;
  inactiveZone?: number;
  proximity?: number;
  spread?: number;
  variant?: "default" | "white";
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  movementDuration?: number;
  borderWidth?: number;
  forceActive?: boolean;
}

function shortestAngleDelta(from: number, to: number) {
  return ((((to - from) % 360) + 540) % 360) - 180;
}

const GlowingEffect = memo(function GlowingEffect({
  blur = 0,
  inactiveZone = 0.7,
  proximity = 0,
  spread = 20,
  variant = "default",
  glow = false,
  className,
  movementDuration = 2,
  borderWidth = 1,
  disabled = true,
  forceActive = false,
}: GlowingEffectProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPosition = useRef({ x: 0, y: 0 });
  const frameRef = useRef<number>(0);
  const angleRef = useRef(0);

  const handleMove = useCallback(
    (event?: MouseEvent | PointerEvent | { x: number; y: number }) => {
      if (!containerRef.current) return;
      if (forceActive) return;
      if (frameRef.current) cancelAnimationFrame(frameRef.current);

      frameRef.current = requestAnimationFrame(() => {
        const element = containerRef.current;
        if (!element) return;

        const { left, top, width, height } = element.getBoundingClientRect();
        const mouseX = event?.x ?? lastPosition.current.x;
        const mouseY = event?.y ?? lastPosition.current.y;
        if (event) lastPosition.current = { x: mouseX, y: mouseY };

        const centerX = left + width * 0.5;
        const centerY = top + height * 0.5;
        const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;
        if (Math.hypot(mouseX - centerX, mouseY - centerY) < inactiveRadius) {
          element.style.setProperty("--active", "0");
          return;
        }

        const isActive =
          mouseX > left - proximity &&
          mouseX < left + width + proximity &&
          mouseY > top - proximity &&
          mouseY < top + height + proximity;
        element.style.setProperty("--active", isActive ? "1" : "0");
        if (!isActive) return;

        const targetAngle =
          (180 * Math.atan2(mouseY - centerY, mouseX - centerX)) / Math.PI + 90;
        const nextAngle =
          angleRef.current +
          shortestAngleDelta(angleRef.current, targetAngle) *
            Math.min(1, 0.22 / Math.max(0.01, movementDuration));
        angleRef.current = nextAngle;
        element.style.setProperty("--start", String(nextAngle));
      });
    },
    [forceActive, inactiveZone, movementDuration, proximity],
  );

  useEffect(() => {
    if (disabled) return;

    const element = containerRef.current;
    if (forceActive) {
      element?.style.setProperty("--active", "1");
      element?.style.setProperty("--start", "236.84");
      return;
    }

    const handleScroll = () => handleMove();
    const handlePointerMove = (event: PointerEvent) => handleMove(event);
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.body.addEventListener("pointermove", handlePointerMove, {
      passive: true,
    });

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      window.removeEventListener("scroll", handleScroll);
      document.body.removeEventListener("pointermove", handlePointerMove);
    };
  }, [disabled, forceActive, handleMove]);

  // Intentional decorative art: these hex stops are bespoke gradient colours
  // with no 1:1 design-token equivalent. They are not UI chrome — do not
  // replace with semantic tokens.
  const gradient =
    variant === "white"
      ? "repeating-conic-gradient(from 236.84deg at 50% 50%, rgba(255,255,255,0.95), rgba(255,255,255,0.95) calc(25% / var(--repeating-conic-gradient-times)))"
      : `radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),
         radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),
         radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%),
         radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),
         repeating-conic-gradient(
           from 236.84deg at 50% 50%,
           #dd7bbb 0%,
           #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
           #5a922c calc(50% / var(--repeating-conic-gradient-times)),
           #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
           #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
         )`;

  return (
    <>
      <div
        className={cn(
          "pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity",
          glow && "opacity-100",
          variant === "white" && "border-[color:var(--color-readable-overlay-primary)]",
          disabled && "!block",
        )}
        aria-hidden="true"
      />
      <div
        ref={containerRef}
        style={
          {
            "--blur": `${blur}px`,
            "--spread": spread,
            "--start": "0",
            "--active": forceActive ? "1" : "0",
            "--glowingeffect-border-width": `${borderWidth}px`,
            "--repeating-conic-gradient-times": "5",
            "--gradient": gradient,
          } as CSSProperties
        }
        className={cn(
          "denker-glowing-effect pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity",
          glow && "opacity-100",
          blur > 0 && "blur-[var(--blur)]",
          className,
          disabled && "!hidden",
        )}
        aria-hidden="true"
      >
        <div className="denker-glowing-effect-mask rounded-[inherit]" />
      </div>
    </>
  );
});

export { GlowingEffect };
export type { GlowingEffectProps };
